import * as React from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { RANGE_FIELD_DEFS, type RangeFieldDef } from '../lib/range-field-defs';

/**
 * Numeric range filter "sidecar" params (CU-86cataqt9, extracted to a neutral
 * home for CU-86ca9k73a).
 * ==========================================================================
 * Each `range` FilterField (packages/ui RangePill) needs a `[min, max]` value
 * pair, but persists to the URL as two SCALAR params (`<id>_min`/`<id>_max`),
 * modeled on the near-POI sidecar pattern (`usePoiFilterParams`) and the
 * catalog's own `useAvailabilityRangeParams`. One `[min, max]` pair per
 * dimension in `RANGE_FIELD_DEFS` (`src/lib/range-field-defs.ts`) — reading
 * and writing ALL dimensions together (like `useSharedFilters`) rather than
 * one hook per dimension, so a page only needs a single hook call.
 *
 * Neutral home so both the catalog (`features/media-units/hooks/
 * use-catalog-sidecar-params.ts`, which re-exports these) and the offer
 * units-tab (`features/offers/units-tab/units-tab-page.tsx`) share one source
 * of truth instead of two copies — cross-feature import of
 * `features/media-units` from `features/offers` is forbidden
 * (WEB_ARCHITECTURE.md:36).
 */

// Cast helper: navigate() without `from` has no concrete search type. Every setter
// below uses the FUNCTIONAL search updater (search reads LIVE state at commit time
// instead of a captured snapshot) so a setter that fires close to another
// filter-state setter never clobbers what the other just wrote (CU-86cataqt9 fix).
type AnyNavigate = (opts: {
  search: (prev: Record<string, unknown>) => Record<string, unknown>;
  replace: boolean;
}) => void;

function findRangeFieldDef(id: string): RangeFieldDef | undefined {
  return RANGE_FIELD_DEFS.find((def) => def.id === id);
}

/** Reads every declared range dimension's `[min, max]` pair from the URL search object. */
export function readRangeFilterValues(search: Record<string, unknown>): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  for (const def of RANGE_FIELD_DEFS) {
    const rawMin = search[def.minKey];
    const rawMax = search[def.maxKey];
    const min = typeof rawMin === 'number' || typeof rawMin === 'string' ? String(rawMin) : '';
    const max = typeof rawMax === 'number' || typeof rawMax === 'string' ? String(rawMax) : '';
    if (min || max) result[def.id] = [min, max];
  }
  return result;
}

/**
 * Maps one range field's `[min, max]` value back to its two scalar URL params.
 * Either side absent/blank deletes that side's param (one-sided ranges keep
 * only the set side); both blank deletes both.
 */
export function buildNextRangeFilterParams(
  current: Record<string, unknown>,
  fieldId: string,
  value: string[],
): Record<string, unknown> {
  const def = findRangeFieldDef(fieldId);
  if (!def) return current;
  const next = { ...current };
  const min = value[0]?.trim();
  const max = value[1]?.trim();
  if (min) next[def.minKey] = Number(min);
  else delete next[def.minKey];
  if (max) next[def.maxKey] = Number(max);
  else delete next[def.maxKey];
  return next;
}

/**
 * Applies EVERY range dimension present in a merged FilterPanel value object
 * (as delivered by FilterPanel's `onChange` — the full `{...value, [id]: values}`
 * merge) back onto the URL search params, one dimension at a time. Dimensions
 * absent from `mergedValue` are left untouched.
 */
export function buildNextRangeFilterValues(
  current: Record<string, unknown>,
  mergedValue: Record<string, string[]>,
): Record<string, unknown> {
  let next = current;
  for (const def of RANGE_FIELD_DEFS) {
    const pair = mergedValue[def.id];
    if (pair === undefined) continue;
    next = buildNextRangeFilterParams(next, def.id, pair);
  }
  return next;
}

/**
 * Reads all range dimensions as a merged `Record<id, [min, max]>` FilterPanel
 * value and returns a setter accepting the SAME merged-value shape FilterPanel's
 * `onChange` delivers (matches `useSharedFilters`'s contract so the page can
 * dispatch one combined `onChange` to every sidecar setter).
 */
export function useRangeFilterParams(): [
  Record<string, string[]>,
  (value: Record<string, string[]>) => void,
] {
  const search = useSearch({ strict: false, structuralSharing: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const value = React.useMemo(() => readRangeFilterValues(search), [search]);
  const setValue = React.useCallback(
    (mergedValue: Record<string, string[]>) => {
      void (navigate as unknown as AnyNavigate)({
        search: (prev) => buildNextRangeFilterValues(prev, mergedValue),
        replace: true,
      });
    },
    [navigate],
  );
  return [value, setValue];
}
